/**
 * ULID — lexicographically sortable, 128-bit, collision-resistant identifiers.
 *
 * Chosen over auto-increment because sequential integers must never appear in
 * a public QR payload or URL (IDOR / enumeration), and over UUIDv4 because D1
 * index locality matters for time-ordered tables like task_events.
 *
 * Uses WebCrypto only — works identically in Workers, browsers and Node 20+.
 */

const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ'; // Crockford base32, no I L O U
const ENCODING_LEN = 32;
const TIME_LEN = 10;
const RANDOM_LEN = 16;

function encodeTime(now: number, len: number): string {
  if (!Number.isFinite(now) || now < 0) throw new RangeError('ulid: invalid timestamp');
  let mod: number;
  let str = '';
  let t = Math.floor(now);
  for (let i = len; i > 0; i--) {
    mod = t % ENCODING_LEN;
    str = ENCODING.charAt(mod) + str;
    t = (t - mod) / ENCODING_LEN;
  }
  return str;
}

function randomChars(len: number): string {
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  let str = '';
  for (let i = 0; i < len; i++) {
    str += ENCODING.charAt(bytes[i]! % ENCODING_LEN);
  }
  return str;
}

/** Generate a ULID. `seedTime` must be a SERVER timestamp in milliseconds. */
export function ulid(seedTime?: number): string {
  const t = seedTime ?? Date.now();
  return encodeTime(t, TIME_LEN) + randomChars(RANDOM_LEN);
}

const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/;

export function isUlid(value: unknown): value is string {
  return typeof value === 'string' && ULID_RE.test(value);
}

/** Milliseconds encoded in the ULID prefix. Useful for cheap age checks. */
export function ulidTime(id: string): number {
  if (!isUlid(id)) throw new TypeError('ulid: malformed id');
  let t = 0;
  for (let i = 0; i < TIME_LEN; i++) {
    t = t * ENCODING_LEN + ENCODING.indexOf(id.charAt(i));
  }
  return t;
}

/** URL-safe opaque token (default 256 bits). Never used as a database id. */
export function randomToken(bytes = 32): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  let bin = '';
  for (const b of buf) bin += String.fromCharCode(b);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** Short human-readable code for QR slips: 6 chars, no ambiguous glyphs. */
export function shortCode(len = 6): string {
  const alphabet = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  let out = '';
  for (let i = 0; i < len; i++) out += alphabet.charAt(bytes[i]! % alphabet.length);
  return out;
}
